import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { login, verify2FA, requires2FA, tempToken, isAuthenticated, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect if already authenticated
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Don't render login form if authenticated (will redirect)
  if (isAuthenticated) {
    return null;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      await login(email, password);
      // If 2FA is required, don't navigate yet
      if (!requires2FA) {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (!tempToken) {
        throw new Error('Temporary token not found');
      }
      await verify2FA(tempToken, code);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : '2FA verification failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'white',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 10 }}>
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              position: 'relative',
              borderRadius: 3,
            }}
          >
            {/* Application Logo - Prominent at top */}
            <Box
              component="img"
              src="/emslogo.png"
              alt="Email Management System"
              sx={{
                maxWidth: '280px',
                width: '100%',
                height: 'auto',
                mb: 4,
                objectFit: 'contain',
              }}
            />

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {!requires2FA ? (
            <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                required
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handle2FAVerify} sx={{ width: '100%' }}>
              <Card sx={{ mb: 3, backgroundColor: '#f9f9f9' }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Two-Factor Authentication Required
                  </Typography>
                  <Typography variant="body2">
                    Please enter the 6-digit code from your authenticator app.
                  </Typography>
                </CardContent>
              </Card>
              <TextField
                margin="normal"
                required
                fullWidth
                id="code"
                label="6-Digit Code"
                name="code"
                autoFocus
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                disabled={isLoading}
                inputProps={{
                  maxLength: 6,
                  pattern: '[0-9]{6}',
                }}
                helperText="Enter the 6-digit code from your authenticator app"
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={isLoading || code.length !== 6}
              >
                {isLoading ? <CircularProgress size={24} /> : 'Verify Code'}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => {
                  setError(null);
                  setCode('');
                  window.location.reload();
                }}
                disabled={isLoading}
              >
                Back to Login
              </Button>
            </Box>
          )}
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

export default LoginPage;

